import { HTMLAttributes, useState, useEffect, Suspense } from "react";
import { useNavigate } from "@tanstack/react-router";
import { CommonInstallflowFrame } from "./common-install-flow-frame";
import { Button } from "@/components/ui/button";
import { BriefcaseBusiness } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useOrganizationsMinimal } from "@/organization/api";
import { OrganizationSelectorInline } from "@/components/OrganizationSelectorInline";
import { OrganizationDataMinimal } from "@/organization/organization";
import { Spinner } from "@/components/ui/spinner";
import {
  installFlowOrganizationId,
  installFlowOrganizationName,
} from "@/installFlow/storage-keys";

function OrganizationContent({
  onOrganizationSelect,
  onContinue,
}: {
  onOrganizationSelect: (org: OrganizationDataMinimal) => void;
  onContinue: () => void;
}) {
  const {
    data: { results: allOrganizations },
  } = useOrganizationsMinimal();

  const [selectedOrg, setSelectedOrg] =
    useState<OrganizationDataMinimal | null>(null);

  const handleSelect = (org: OrganizationDataMinimal) => {
    setSelectedOrg(org);
    onOrganizationSelect(org);
  };

  const isValid = selectedOrg !== null && selectedOrg.id > 0;

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="flex flex-col space-y-2 items-center justify-center text-center">
        <h1 className="text-2xl font-semibold">Select Organization</h1>
        <p className="text-muted-foreground text-sm">
          Choose the organization you are installing for.
        </p>
      </div>

      <Card className="space-y-3 p-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <BriefcaseBusiness className="h-4 w-4" />
          <span>
            {selectedOrg
              ? `Installing for: ${selectedOrg.name}`
              : "No organization selected"}
          </span>
        </div>
        <div className="text-sm text-muted-foreground">
          Select organization:
        </div>
        <OrganizationSelectorInline
          organizations={allOrganizations}
          selectedOrganization={selectedOrg ?? { id: 0, name: "" }}
          onSelect={handleSelect}
        />
      </Card>

      <Button
        variant="installFlow"
        className="w-full h-[50px] p-4 rounded-[25px] text-base font-semibold"
        onClick={onContinue}
        disabled={!isValid}
      >
        Continue
      </Button>
    </div>
  );
}

interface AuthProps extends HTMLAttributes<HTMLDivElement> {}

export function SelectOrganization({ className }: AuthProps) {
  const navigate = useNavigate();

  useEffect(() => {
    installFlowOrganizationId.remove();
    installFlowOrganizationName.remove();
  }, []);

  const handleOrganizationSelect = (org: OrganizationDataMinimal) => {
    installFlowOrganizationId.set(String(org.id));
    installFlowOrganizationName.set(org.name);
  };

  const handleContinue = () => {
    navigate({ to: "/installFlow/install-not-started" });
  };

  return (
    <CommonInstallflowFrame className={className}>
      <Suspense
        fallback={
          <div className="flex items-center justify-center py-12">
            <Spinner />
          </div>
        }
      >
        <OrganizationContent
          onOrganizationSelect={handleOrganizationSelect}
          onContinue={handleContinue}
        />
      </Suspense>
    </CommonInstallflowFrame>
  );
}
